import { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { toastErrorLog } from '../../helpers/toastErrorLog'
import { useImagenesPublicacion } from '../../hooks/useImagenesPublicacion.js'
import Imagen from '../imagen/Imagen.jsx'
import Precios from '../reutilizables/precios/Precios'
import { buscarSimilaresPorPedido } from '../servicios/compraService'
import BordeDetalle from '../ui/BordeDetalle'
import LinkButton from '../ui/LinkButton'
import styles from './AnalisisCompra.module.css'

const AnalisisCompra = () => {
  const { idPedido } = useParams()
  const navigate = useNavigate()
  const [datos, setDatos] = useState([])
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState(false)
  const compradorNombre = datos[0]?.compradorNombre

  const { adjuntos, loadingImagenes, fetchImagenes } = useImagenesPublicacion()

  const idsPublicacionesSimilares = useMemo(
    () =>
      datos.flatMap((item) =>
        Array.isArray(item.similares) ? item.similares.map((pub) => pub.idPublicacion) : []
      ),
    [datos]
  )

  useEffect(() => {
    const cargar = async () => {
      try {
        const respuesta = await buscarSimilaresPorPedido(idPedido)

        if (!respuesta) {
          toastErrorLog(null, `No se encontró el pedido Nº ${idPedido} o fue eliminado.`, 'pedidos')
          navigate('/Pedidos', { replace: true })
          return
        }

        setDatos(respuesta.data)
      } catch {
        setError(true)
        navigate('/Pedidos', { replace: true })
      } finally {
        setCargando(false)
      }
    }
    cargar()
  }, [idPedido])

  useEffect(() => {
    if (idsPublicacionesSimilares.length === 0) {
      return
    }
    fetchImagenes({ idPublicaciones: idsPublicacionesSimilares })
  }, [idsPublicacionesSimilares])

  const titulo = `Análisis de compra — Pedido #${idPedido}`
  const handleCerrar = () => navigate('/Pedidos')

  if (cargando) {
    return (
      <BordeDetalle titulo={titulo} onCerrarClick={handleCerrar}>
        <div className={styles.estado}>Cargando análisis...</div>
      </BordeDetalle>
    )
  }

  if (error) {
    return (
      <BordeDetalle titulo={titulo} onCerrarClick={handleCerrar}>
        <div className={styles.estado}>No se pudo cargar el análisis.</div>
      </BordeDetalle>
    )
  }
  return (
    <BordeDetalle titulo={titulo} onCerrarClick={handleCerrar}>
      {compradorNombre && (
        <p className={styles.metaInfo}>
          <i className="fa fa-user" aria-hidden="true" /> Comprador: {compradorNombre}
        </p>
      )}

      {datos.length === 0 ? (
        <div className={styles.estado}>Compraste la mejor opción!</div>
      ) : (
        datos.map((item, index) => {
          const tieneSimilares = Array.isArray(item.similares) && item.similares.length > 0
          const tituloItem = item.titulo ?? `Item #${item.idPedidoItem}`

          if (!tieneSimilares) {
            return (
              <div key={item.idPedidoItem} className={styles.seccion}>
                {index > 0 && <hr className={styles.separador} />}
                <h4 className={styles.subtitulo}>{tituloItem}</h4>
                <p className={styles.metaInfo}>- Compraste la mejor opción!</p>
              </div>
            )
          }

          return (
            <div key={item.idPedidoItem} className={styles.seccion}>
              {index > 0 && <hr className={styles.separador} />}
              <h4 className={styles.subtitulo}>Oportunidades perdidas — {tituloItem}</h4>
              <p className={styles.metaInfo}>- Productos similares:</p>
              <div className={styles.grilla}>
                {item.similares.map((pub) => (
                  <div key={pub.idPublicacion}>
                    <LinkButton to={`/Publicacion/${pub.idPublicacion}`} className={styles.card}>
                      <div className={styles.cardImagen}>
                        <Imagen
                          className={styles.imagen}
                          adjunto={adjuntos.find(
                            (a) => a.idPublicacion === Number(pub.idPublicacion)
                          )}
                          loadingImagenes={loadingImagenes}
                        />
                      </div>
                      <div className={styles.cardCuerpo}>
                        <p className={styles.cardTitulo}>{pub.titulo}</p>
                        <p className={styles.cardVendedor}>
                          <i className="fa fa-user" aria-hidden="true" /> {pub.razonSocial?.trim()}
                        </p>
                        <Precios
                          alicuota={pub.alicuota}
                          precio={pub.precioUnitario}
                          monedaSimbolo={pub.monedaSimbolo?.trim() ?? '$'}
                          className={styles.cardPrecio}
                          classNameValor={styles.cardPrecioValor}
                          classNameDescuento={styles.cardPrecioDescuento}
                          showSinIVA={true}
                        />
                        {pub.cantDisponible !== null && (
                          <p className={styles.cardStock}>Stock: {pub.cantDisponible}</p>
                        )}
                      </div>
                    </LinkButton>
                  </div>
                ))}
              </div>
            </div>
          )
        })
      )}
    </BordeDetalle>
  )
}

export default AnalisisCompra
